import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { AddressFilterPart, AddressFilterValue } from '@scrambl/shared'
import {
  ADDRESS_FILTER_PART_LABELS,
  ADDRESS_FILTER_PARTS,
  isAddressFilterValue,
} from '@scrambl/shared'

import type { ColumnFilterValue } from '../types'

const DEFAULT_FILTER: AddressFilterValue = {
  part: 'city',
  operator: 'contains',
  text: '',
}

/**
 * Column filter body for ADDRESS custom fields: pick the part (city, state,
 * postal code, country, street or any), contains/is, then the text. An empty
 * text leaves the filter unset.
 */
export function AddressFilterEditor({
  value,
  onChange,
  onSubmit,
}: {
  value: ColumnFilterValue | null | undefined
  onChange: (value: ColumnFilterValue | null) => void
  onSubmit?: () => void
}) {
  const draft =
    value?.type === 'address' && isAddressFilterValue(value.value)
      ? value.value
      : DEFAULT_FILTER

  // Blank text is kept as a draft; the popover's Apply treats it as "clear"
  const update = (patch: Partial<AddressFilterValue>) =>
    onChange({ type: 'address', value: { ...draft, ...patch } })

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Part</Label>
        <div className="flex flex-wrap gap-1">
          {ADDRESS_FILTER_PARTS.map((part: AddressFilterPart) => (
            <Button
              key={part}
              type="button"
              size="sm"
              variant={draft.part === part ? 'default' : 'outline'}
              className="h-7 px-2 text-xs"
              onClick={() => update({ part })}
            >
              {ADDRESS_FILTER_PART_LABELS[part]}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex gap-1">
        {(['contains', 'equals'] as const).map(operator => (
          <Button
            key={operator}
            type="button"
            size="sm"
            variant={draft.operator === operator ? 'default' : 'outline'}
            className="h-7 flex-1 text-xs"
            onClick={() => update({ operator })}
          >
            {operator === 'contains' ? 'Contains' : 'Is exactly'}
          </Button>
        ))}
      </div>
      <Input
        type="text"
        aria-label={`${ADDRESS_FILTER_PART_LABELS[draft.part]} filter text`}
        placeholder={
          draft.part === 'country'
            ? 'Country name or code'
            : `${ADDRESS_FILTER_PART_LABELS[draft.part]}…`
        }
        className="h-8"
        value={draft.text}
        onChange={e => update({ text: e.target.value })}
        onKeyDown={e => {
          if (e.key === 'Enter') onSubmit?.()
        }}
        autoFocus
      />
    </div>
  )
}
